import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Copy, Trash2, Pencil, Download, Archive, Send, Share2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { useEntityLabels } from '../hooks/useEntityLabels.js';
import { QuoteStatusBadge } from '../components/QuoteStatusBadge.js';
import {
  listQuotes,
  listResellers,
  deleteQuote,
  duplicateQuote,
  updateQuote,
  getConnectedResellers,
  sendQuoteToReseller,
  ApiError,
} from '../api/client.js';
import type { QuoteListItem, Reseller, ConnectedReseller } from '../api/client.js';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function QuotesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<QuoteListItem[]>([]);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [resellerFilter, setResellerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Delete modal
  const [deletingQuote, setDeletingQuote] = useState<QuoteListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [success, setSuccess] = useState('');

  // Send to Reseller modal
  const [sendingQuote, setSendingQuote] = useState<QuoteListItem | null>(null);
  const [connectedResellers, setConnectedResellers] = useState<ConnectedReseller[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [sendProjectRef, setSendProjectRef] = useState('');
  const [sendNotes, setSendNotes] = useState('');
  const [sendLoading, setSendLoading] = useState(false);

  const labels = useEntityLabels();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isManufacturer = user?.tenantType === 'manufacturer';

  const loadQuotes = useCallback(
    async (search?: string, resellerId?: string, status?: string, archived?: boolean) => {
      try {
        const data = await listQuotes({
          search: search || undefined,
          resellerId: resellerId || undefined,
          status: status || undefined,
          archived,
        });
        setQuotes(data.quotes);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load quotes');
      }
    },
    [],
  );

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [, resellerData] = await Promise.all([
        loadQuotes(undefined, undefined, undefined, false),
        listResellers({ active: true }).catch(() => ({ resellers: [] as Reseller[] })),
      ]);
      setResellers(resellerData.resellers);
      setLoading(false);
    };
    init();
  }, [loadQuotes]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadQuotes(searchTerm, resellerFilter, statusFilter, showArchived);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, resellerFilter, statusFilter, showArchived, loadQuotes]);

  const handleDuplicate = async (quote: QuoteListItem) => {
    try {
      const result = await duplicateQuote(quote.id);
      navigate(`/quotes/${result.quote.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to duplicate quote');
    }
  };

  const handleArchive = async (quoteId: string, archive: boolean) => {
    try {
      await updateQuote(quoteId, { archived: archive });
      loadQuotes(searchTerm, resellerFilter, statusFilter, showArchived);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update quote');
    }
  };

  const handleDownloadPdf = async (q: QuoteListItem) => {
    setPdfLoadingId(q.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${q.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quote-${q.displayId || q.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to generate PDF');
    }
    setPdfLoadingId(null);
  };

  const handleDelete = async () => {
    if (!deletingQuote) return;
    setDeleteLoading(true);
    try {
      await deleteQuote(deletingQuote.id);
      setDeletingQuote(null);
      await loadQuotes(searchTerm, resellerFilter, statusFilter, showArchived);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete quote');
      setDeletingQuote(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openSendDialog = async (quote: QuoteListItem) => {
    setSendingQuote(quote);
    setSendProjectRef(quote.projectName || '');
    setSendNotes('');
    setSelectedRecipient('');
    if (connectedResellers.length === 0) {
      try {
        const data = await getConnectedResellers();
        setConnectedResellers(data.resellers);
        if (data.resellers.length > 0) {
          setSelectedRecipient(data.resellers[0]!.id);
        }
      } catch {
        // ignore — will show empty dropdown
      }
    } else if (connectedResellers.length > 0) {
      setSelectedRecipient(connectedResellers[0]!.id);
    }
  };

  const handleSendToReseller = async () => {
    if (!sendingQuote || !selectedRecipient) return;
    setSendLoading(true);
    try {
      await sendQuoteToReseller({
        quoteId: sendingQuote.id,
        recipientTenantId: selectedRecipient,
        projectReference: sendProjectRef || undefined,
        notes: sendNotes || undefined,
      });
      const recipientName =
        connectedResellers.find((r) => r.id === selectedRecipient)?.name || 'reseller';
      setSuccess(`Quote sent to ${recipientName}`);
      setSendingQuote(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send quote');
    } finally {
      setSendLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-100">Quotes</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/quotes/new')}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            New Quote
          </button>
        </div>
      </div>

      {success && (
        <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
          {success}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {/* Filter Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search quotes..."
            className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pl-9 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <select
          value={resellerFilter}
          onChange={(e) => setResellerFilter(e.target.value)}
          className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All {labels.Resellers}</option>
          {resellers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="ready">Ready</option>
          <option value="final">Final</option>
        </select>

        {/* Archive toggle */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowArchived(false)}
            className={`rounded-l-lg px-3 py-2 text-sm ${!showArchived ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-500 hover:text-gray-300'}`}
          >
            Active
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`rounded-r-lg px-3 py-2 text-sm ${showArchived ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-500 hover:text-gray-300'}`}
          >
            Archived
          </button>
        </div>
      </div>

      {/* Quotes Table */}
      {quotes.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <p className="text-gray-500">
            {searchTerm || resellerFilter || statusFilter
              ? 'No quotes match your filters.'
              : showArchived
                ? 'No archived quotes.'
                : 'No quotes yet. Create your first quote to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3">Quote #</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">{labels.Reseller}</th>
                <th className="px-4 py-3 text-center">Items</th>
                <th className="px-4 py-3 text-right">Total MSRP</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => (
                <tr
                  key={quote.id}
                  className="border-b border-gray-800 hover:bg-gray-800/30"
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      className="font-semibold text-brand-400 hover:text-brand-300"
                    >
                      {quote.displayId || '\u2014'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {quote.projectName || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {quote.resellerName || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-400">
                    {quote.lineItemCount}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-100">
                    {formatCurrency(quote.totalMsrp)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <QuoteStatusBadge status={quote.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(quote.updatedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => navigate(`/quotes/${quote.id}`)}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(quote)}
                        disabled={pdfLoadingId === quote.id}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300 disabled:opacity-50"
                        title="Download PDF"
                      >
                        <Download className={`h-4 w-4 ${pdfLoadingId === quote.id ? 'animate-pulse' : ''}`} />
                      </button>
                      {isManufacturer && (
                        <button
                          onClick={() => openSendDialog(quote)}
                          className="rounded p-1.5 text-brand-400 hover:bg-gray-800 hover:text-brand-300"
                          title="Share"
                        >
                          <Share2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDuplicate(quote)}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleArchive(quote.id, !quote.archived)}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                        title={quote.archived ? 'Unarchive' : 'Archive'}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => setDeletingQuote(quote)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Delete Quote</h2>
            <p className="mb-6 text-sm text-gray-400">
              Delete quote &ldquo;{deletingQuote.displayId}&rdquo;
              {deletingQuote.projectName ? ` (${deletingQuote.projectName})` : ''}? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingQuote(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Reseller Modal */}
      {sendingQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-100">Send Quote to Reseller</h3>
            <p className="mt-1 text-sm text-gray-400">
              Send &ldquo;{sendingQuote.displayId}&rdquo; to a connected reseller.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-400">Reseller</label>
                {connectedResellers.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No connected resellers. Resellers connect through Marketplace catalog subscriptions.
                  </p>
                ) : (
                  <select
                    value={selectedRecipient}
                    onChange={(e) => setSelectedRecipient(e.target.value)}
                    className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    {connectedResellers.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.companyName || r.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Project Reference</label>
                <input
                  type="text"
                  value={sendProjectRef}
                  onChange={(e) => setSendProjectRef(e.target.value)}
                  placeholder="e.g., Johnson Hospital Phase 2"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Notes (optional)</label>
                <textarea
                  value={sendNotes}
                  onChange={(e) => setSendNotes(e.target.value)}
                  placeholder="Any context for the reseller..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setSendingQuote(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSendToReseller}
                disabled={sendLoading || !selectedRecipient}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {sendLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
